import * as React from "react";
import { DeviceData } from "../models/DeviceData";
import { DiagramApiController } from "../models/DiagramApiController.generated";
import { LSM_LogicSolverSheet } from "../models/LSM_LogicSolverSheet";
import { UpdateLogicSolverDataArgs } from "../models/UpdateLogicSolverDataArgs";
import { SilModal } from "./SilModal";
import { MasterPoolMismatchItem } from "../models/MasterPoolMismatchItem";

interface Props {
  device?: DeviceData;
  deviceSheet?: LSM_LogicSolverSheet;
  closeModal: () => void;
  onUpdateLogicSolverDataClick?: (args: UpdateLogicSolverDataArgs) => Promise<boolean>;
  onDeviceSheetRestoreClick?: (
    ptProtectiveFunctionId: number
  ) => Promise<void>;
  masterPoolMismatchItems?: MasterPoolMismatchItem[];
  readOnly: boolean;
}

interface State {
  selectedVoting: string;
  votingOptions: string[];
  selectedProofTestingInterval: number | string;
  proofTestingIntervalOptions: number[];
  pfdAvgDisplay: string;
  pfdAvg: number;
  strDisplay: string;
  str: number;
  hft?: number;
  closeFocus: boolean;
  isDeviceSheetMatch: boolean;
}

// TODO: When opened the Voting and Proof Testing Interval (yr) fields
// take a little time to load. But, we already have this info and we don't
// need to call into the server to get them. We just need to call to get
// the other options available. We can improve to show existing data on open later
export class LogicModal extends React.Component<Props, State> {

  private tagNameRef = React.createRef<HTMLInputElement>();
  private votingRef = React.createRef<HTMLSelectElement>();
  private proofTestingIntervalRef = React.createRef<HTMLSelectElement>();
  private isBound: boolean;

  constructor(props: Props) {
    super(props);

    this.isBound = this.props.device != null;

    const selectedVoting = this.isBound
      ? props.device.ptProtectiveFunction.voting ? props.device.ptProtectiveFunction.voting : "Select"
      : "Select";
    const votingOptions = this.isBound
      ? props.device.ptProtectiveFunction.voting ? [props.device.ptProtectiveFunction.voting] : []
      : [props.deviceSheet.voting];
    const selectedProofTestingInterval = this.isBound
      ? props.device.ptProtectiveFunction.proofTestingInterval || "Select"
      : "Select";
    const proofTestingIntervalOptions = this.isBound
      ? props.device.ptProtectiveFunction.proofTestingInterval ? [props.device.ptProtectiveFunction.proofTestingInterval] : []
      : [];
    const pfdAvgDisplay = this.isBound
      ? props.device.ptProtectiveFunction.pfdAverageDisplay
      : props.deviceSheet.pfdAverageDisplay;
    const pfdAvg = this.isBound
      ? props.device.ptProtectiveFunction.pfdAverage
      : props.deviceSheet.pfdAverage;
    const strDisplay = this.isBound
      ? props.device.ptProtectiveFunction.strDisplay
      : props.deviceSheet.strDisplay;
    const str = this.isBound
      ? props.device.ptProtectiveFunction.stR1Year
      : props.deviceSheet.stR1Year;
    const hft = this.isBound
      ? props.device.ptProtectiveFunction.hft
      : props.deviceSheet.hft;

    this.state = {
      selectedVoting,
      votingOptions,
      selectedProofTestingInterval,
      proofTestingIntervalOptions,
      pfdAvgDisplay,
      pfdAvg,
      strDisplay,
      str,
      hft,
      closeFocus: false,
      isDeviceSheetMatch: true
    };
    this.handleRestoreDeviceSheet = this.handleRestoreDeviceSheet.bind(this);
  }

  // When the component mounts, call the server to get a list of the possible voting
  // options for the logic solver passed in via props
  public componentDidMount() {
    if (this.props.masterPoolMismatchItems && this.props.masterPoolMismatchItems.find(x => x.protectiveFunctionId == this.props.device.ptProtectiveFunction.protectiveFunctionID)) {
      this.setState({ isDeviceSheetMatch: false })
    }
    if (this.isBound) {
      const ptpf = this.props.device.ptProtectiveFunction;
      DiagramApiController.bindLogicvoting(ptpf.protectiveFunctionID)
        .then((votingOptions) => this.setState({ votingOptions }));

      if (ptpf.voting != null) {
        DiagramApiController.bindLogicTIYears({
          logicDeviceId: ptpf.deviceId,
          logicSource: ptpf.source,
          logicVoting: ptpf.voting,
          protectiveFunctionIdLogic: ptpf.protectiveFunctionID,
        }).then((proofTestingIntervalOptions) => this.setState({ proofTestingIntervalOptions }));
      }
    } else {
      DiagramApiController.bindLogicvotings(this.props.deviceSheet.source, this.props.deviceSheet.deviceID)
        .then((votingOptions) => this.setState({ votingOptions }));
    }
  }

  private handleRestoreDeviceSheet() {
    const device = { ...this.props.device };
    this.props.onDeviceSheetRestoreClick(device.ptProtectiveFunction.protectiveFunctionID).then(() => this.props.closeModal());
  }

  // When a new voting is selected, call the server to get a list of the possible
  // testing intervals available for the logic solver and newly selected voting configuration
  public getProofTestIntervalOptions = () => {
    const selectedVoting = this.votingRef.current.value;
    const logicDeviceId = this.isBound
      ? this.props.device.ptProtectiveFunction.deviceId
      : this.props.deviceSheet.deviceID;
    const logicSource = this.isBound
      ? this.props.device.ptProtectiveFunction.source
      : this.props.deviceSheet.source;
    const protectiveFunctionIdLogic = this.isBound
      ? this.props.device.ptProtectiveFunction.protectiveFunctionID
      : 0;

    DiagramApiController.bindLogicTIYears({
      logicDeviceId,
      logicSource,
      logicVoting: selectedVoting,
      protectiveFunctionIdLogic,
    }).then((proofTestingIntervalOptions) => this.setState({
      selectedVoting,
      proofTestingIntervalOptions,
    }));
  }

  // When a new proof testing interval is selected, call the server to get the PFDavg
  // STR and HFT of the selected Logic solver, voting, and proof testing interval combination
  public getPfdStrHft = () => {
    const selectedProofTestingInterval = Number(this.proofTestingIntervalRef.current.value);

    const logicDeviceID = this.isBound
      ? this.props.device.ptProtectiveFunction.deviceId
      : this.props.deviceSheet.deviceID;

    const logicSource = this.isBound
      ? this.props.device.ptProtectiveFunction.source
      : this.props.deviceSheet.source;

    const ptProtectiveFunctionId = this.isBound
      ? this.props.device.ptProtectiveFunction.protectiveFunctionID
      : 0;

    DiagramApiController.bindLogicPfdandStr({
      logicDeviceID,
      logicSource,
      logicVoting: this.state.selectedVoting,
      ptProtectiveFunctionId,
      logicTIYears: selectedProofTestingInterval,
    })
      .then((logicSolverDataSheet) => this.setState({
        pfdAvg: logicSolverDataSheet.pfdAverage,
        pfdAvgDisplay: logicSolverDataSheet.pfdAverageDisplay,
        str: logicSolverDataSheet.stR1Year,
        strDisplay: logicSolverDataSheet.strDisplay,
        hft: logicSolverDataSheet.hft,
        selectedProofTestingInterval,
      }));
  }

  // Send the updated logic solver data to the server
  public handleSubmitButtonClick = () => {
    if (this.props.readOnly) { return; }

    const tagName = this.tagNameRef.current.value;
    const voting = this.votingRef.current.value;
    const proofTestingInterval = Number(this.proofTestingIntervalRef.current.value);

    if (tagName == null || voting == null || proofTestingInterval == null) {
      // show something to the user?
      return;
    }

    const ptpf = this.props.device.ptProtectiveFunction;

    this.props.onUpdateLogicSolverDataClick({
      protectiveFunctionId: ptpf.protectiveId_FK,
      ptProtectiveFunctionId: ptpf.protectiveFunctionID,
      tagName,
      voting: voting === "Select" ? null : voting,
      proofTestingInterval: this.proofTestingIntervalRef.current.value === "Select" ? 0 : proofTestingInterval,
      pfdAverage: this.state.pfdAvg,
      str: this.state.str,
      hft: this.state.hft,
    })
      .then((result) => {
        if (result) {
          this.props.closeModal();
        } else {
          // Display something to the user
          console.log("Could not update LS");
        }
      });
  }

  public onMouseEnter = () => {
    this.setState({
      closeFocus: true,
    });
  }

  public onMouseLeave = () => {
    this.setState({
      closeFocus: false,
    });
  }

  public render() {
    const device = this.props.device;
    const deviceSheet = this.props.deviceSheet;

    const smallAndBold: React.CSSProperties = {
      fontSize: "small",
      fontWeight: "bold",
    };

    const logicSolverId = this.isBound ? device.ptProtectiveFunction.deviceId : deviceSheet.deviceID;
    const logicSolverType = this.isBound ? device.ptProtectiveFunction.logicSolverType : deviceSheet.deviceType;
    const tagName = this.isBound ? device.ptProtectiveFunction.tageName : "";
    const notes = this.isBound ? device.pmProtectiveFunction.dnts : deviceSheet.deviceDescription;
    const dataSource = this.isBound ? device.ptProtectiveFunction.source : deviceSheet.source;
    const hft = this.state.hft != null ? this.state.hft : "";

    return (
      <SilModal title="Logic Solver" width={650} closeModal={this.props.closeModal}>
        <table id="logicSolverModal">
          <tbody><tr>
            <td>
              <table>
                <tbody><tr>
                  <td>
                    <label htmlFor="txt_tbllogicsolverid" style={{ fontWeight: 1000 }}>Logic Solver ID:</label>
                  </td>
                  <td>
                    <input name="txt_tbllogicsolverid" defaultValue={logicSolverId} type="text" readOnly id="txt_tbllogicsolverid" style={{ borderStyle: "None", borderBottom: "1px solid #000" }} />
                  </td>
                </tr>
                  <tr>
                    <td style={{ paddingTop: "10px" }}>
                      <label htmlFor="txt_tbllogictype" style={{ fontWeight: 1000 }}>Logic Solver Type:</label>
                    </td>
                    <td style={{ paddingTop: "10px" }}>
                      <textarea name="txt_tbllogictype" defaultValue={logicSolverType} rows={2} cols={20} readOnly id="txt_tbllogictype" style={{ borderStyle: "None", height: "40px", width: "397px", borderBottom: "1px solid #000" }} />
                    </td>
                  </tr>
                </tbody></table>
            </td>
          </tr>
            <tr>
              <td>
                <div style={{ background: "#90b0f4" }}>
                  <label style={{ paddingLeft: "10px", ...smallAndBold }}>Configurations:</label>
                  <table>
                    <tbody><tr>
                      <td style={{ paddingLeft: "10px" }}>
                        <label htmlFor="txt_tbllogictagname" style={smallAndBold}>Logic Solver Tag:</label>
                      </td>
                      <td style={{ paddingLeft: "100px" }}>
                        <input
                          name="txt_tbllogictagname"
                          defaultValue={tagName}
                          readOnly={!this.isBound}
                          type="text"
                          id="txt_tbllogictagname"
                          style={{ height: "20px", width: "100px" }}
                          ref={this.tagNameRef}
                        />
                      </td>
                    </tr>
                      <tr>
                        <td style={{ paddingLeft: "10px" }}>
                          <label htmlFor="txt_tbllogicvotingddl" style={smallAndBold}>Voting:</label>
                        </td>
                        <td style={{ paddingLeft: "100px" }}>
                          <select
                            id="txt_tbllogicvotingddl"
                            value={this.state.selectedVoting || "Select"}
                            style={{ height: "20px", width: "100px" }}
                            ref={this.votingRef}
                            onChange={this.getProofTestIntervalOptions}
                          >
                            <option hidden disabled value="Select">Select</option>
                              {this.state.votingOptions.map((vo) => {
                                return (
                                  <option
                                    key={vo}
                                    value={vo}>{vo}
                                  </option>
                                );
                            })}
                          </select>

                        </td>
                      </tr>
                      <tr>
                        <td style={{ paddingLeft: "10px" }}>
                          <label htmlFor="txt_tbllogictiddl" style={smallAndBold}>Proof Testing Interval (yr):</label>
                        </td>
                        <td style={{ paddingLeft: "100px" }}>
                          <select
                            id="txt_tbllogictiddl"
                            value={this.state.selectedProofTestingInterval}
                            style={{ height: "20px", width: "100px" }}
                            ref={this.proofTestingIntervalRef}
                            onChange={this.getPfdStrHft}
                          >
                            <option hidden disabled value="Select">Select</option>
                              {this.state.proofTestingIntervalOptions.map((ptio) => {
                                return (
                                  <option
                                    key={ptio}
                                    value={ptio}>
                                    {ptio}
                                  </option>
                                );
                            })}
                          </select>
                        </td>
                      </tr>
                    </tbody></table>
                </div>
              </td>
            </tr>
            <tr>
              <td style={{ paddingTop: "10px" }}>
                <div>
                  <table style={{ background: "#6b932a", borderRadius: "10px", MozBorderRadius: "10px", WebkitBorderRadius: "10px", width: "100%", height: "40px" }}>
                    <tbody>
                      <tr>
                        <td style={{ paddingLeft: "10px" }}>
                          <label htmlFor="txt_tbllogicpfdavg" style={{ fontSize: "small", fontWeight: "bold" }}>PFDavg:</label>
                        </td>
                        <td style={{ paddingLeft: "10px" }}>
                          <input name="txt_tbllogicpfdavg" value={this.state.pfdAvgDisplay} readOnly type="text" id="txt_tbllogicpfdavg" style={{ borderStyle: "None", borderBottom: "1px solid #000", backgroundColor: "#6b932a" }} />
                        </td>
                        <td style={{ paddingLeft: "10px" }}>
                          <label htmlFor="txt_tbllogicstr" style={{ fontSize: "small", fontWeight: "bold" }}>STR:</label>
                        </td>
                        <td style={{ paddingLeft: "10px" }}>
                          <input name="txt_tbllogicstr" value={this.state.strDisplay} readOnly type="text" id="txt_tbllogicstr" style={{ borderStyle: "None", borderBottom: "1px solid #000", backgroundColor: "#6b932a" }} />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <table style={{ width: '100%' }}>
                  <tbody><tr>
                    <td style={{ paddingTop: "10px" }}>
                      <label htmlFor="txt_tbllogicnote" style={{ fontSize: "medium", fontWeight: "bold" }}>Note:</label>
                    </td>
                  </tr>
                    <tr>
                      <td>
                        <textarea name="txt_tbllogicnote" rows={2} cols={20} id="txt_tbllogicnote" style={{ height: "150px", width: "100%" }} defaultValue={notes} />
                      </td>
                    </tr>
                  </tbody></table>
              </td>
            </tr>
            <tr>
              <td>
                <table>
                  <tbody><tr>
                    <td>
                      <label htmlFor="txt_tbllogicsolverid" style={{ fontWeight: 1000 }}>Data Source:</label>
                    </td>
                    <td style={{ paddingLeft: "10px" }}>
                      <input name="txt_tbllogicsource" defaultValue={dataSource} type="text" readOnly id="txt_tbllogicsource" style={{ borderStyle: "None", width: "100px", borderBottom: "1px solid #000" }} />
                    </td>
                    <td style={{ paddingLeft: "50px" }}>
                      <label htmlFor="txt_tbllogicsolverhft" style={{ fontWeight: 1000 }}>HFT:</label>
                    </td>
                    <td style={{ paddingLeft: "10px" }}>
                      <input value={hft} type="text" readOnly style={{ borderStyle: "None", width: "100px", borderBottom: "1px solid #000" }} />
                    </td>
                    <td style={{ width: "200px", paddingTop: "10px", textAlign: 'right' }}>
                      {this.isBound &&
                        <input
                        id="Update_tbllogic"
                        type="submit"
                        value="Update"
                        className="edit-button"
                        onClick={this.handleSubmitButtonClick}
                        disabled={this.props.readOnly}
                      />
                      }
                      {!this.state.isDeviceSheetMatch && (
                        <input
                          className="edit-button"
                          disabled={this.props.readOnly}
                          type="submit"
                          name="Restore_devicesheet_tbldevice"
                          value="Upgrade device sheet"
                          id="Restore_devicesheet_tbldevice"
                          onClick={this.handleRestoreDeviceSheet}
                        />
                      )}
                    </td>
                  </tr>
                  </tbody></table>
              </td>
            </tr>
          </tbody></table>
      </SilModal>
    );
  }
}
